import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Progress, Tooltip, Alert, Spin, Select, Empty, DatePicker, Space, Modal, Button, Statistic, Divider } from 'antd';
import { QuestionCircleOutlined, WarningOutlined, CheckCircleOutlined, CalendarOutlined, DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import { API_BASE, authHeaders } from '../config/api';
import './admin.css';

// Suppress ResizeObserver loop warnings
const resizeObserverErrHandler = (error) => {
  if (error.message && error.message.includes('ResizeObserver loop completed with undelivered notifications')) {
    // Ignore this specific error as it's a non-critical browser warning
    return;
  }
  console.error(error);
};

if (typeof window !== 'undefined') {
  window.addEventListener('error', resizeObserverErrHandler);
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason && event.reason.message && event.reason.message.includes('ResizeObserver loop completed with undelivered notifications')) {
      event.preventDefault();
    }
  });
}

const { Option } = Select;
const { RangePicker } = DatePicker;

const FormAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(null);
  const [selectedWebsite, setSelectedWebsite] = useState(null);
  const [websites, setWebsites] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [specificDate, setSpecificDate] = useState(null);
  const [fieldModalVisible, setFieldModalVisible] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);

  const loadWebsites = async () => {
    try {
      console.log('[FormAnalytics] Loading websites...');
      const res = await fetch(`${API_BASE}/my/websites`, {
        headers: { 'Content-Type': 'application/json', ...authHeaders() }
      });
      const data = await res.json();
      console.log('[FormAnalytics] Websites response:', data);
      
      // Handle both response formats: {websites: [...]} or {items: [...]}
      const websitesList = data.websites || data.items || [];
      
      if (res.ok && websitesList.length > 0) {
        setWebsites(websitesList);
        setSelectedWebsite(websitesList[0].id);
        console.log('[FormAnalytics] Selected website:', websitesList[0].id);
      } else {
        console.log('[FormAnalytics] No websites found');
        setLoading(false);
      }
    } catch (err) {
      console.error('[FormAnalytics] Error loading websites:', err);
      setLoading(false);
    }
  };

  const loadFormAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_BASE}/analytics/form-analytics?website_id=${selectedWebsite}`;
      
      if (specificDate) {
        url += `&date=${specificDate.format('YYYY-MM-DD')}`;
      } else if (dateRange && dateRange[0] && dateRange[1]) {
        url += `&start_date=${dateRange[0].format('YYYY-MM-DD')}&end_date=${dateRange[1].format('YYYY-MM-DD')}`;
      }
      
      console.log('[FormAnalytics] Fetching:', url);
      const res = await fetch(url, {
        headers: { 'Content-Type': 'application/json', ...authHeaders() }
      });
      const data = await res.json();
      console.log('[FormAnalytics] Response status:', res.status);
      console.log('[FormAnalytics] Response data:', data);
      
      if (res.ok && data && data.forms && data.forms.length > 0) {
        // Use real API data
        setFormData(data);
      } else {
        // No data available - set empty state
        console.log('[FormAnalytics] No form analytics data available from API');
        setFormData(null);
      }
    } catch (err) {
      console.error('[FormAnalytics] Error loading form analytics:', err);
      setFormData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedWebsite, specificDate, dateRange]);

  useEffect(() => {
    loadWebsites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedWebsite) {
      loadFormAnalytics();
    }
  }, [selectedWebsite, loadFormAnalytics]);

  const getFrictionColor = (frictionScore) => {
    if (frictionScore >= 70) return '#ef4444'; // High friction - red
    if (frictionScore >= 40) return '#f59e0b'; // Medium friction - orange
    return '#10b981'; // Low friction - green
  };

  const getFrictionLabel = (frictionScore) => {
    if (frictionScore >= 70) return 'High Friction';
    if (frictionScore >= 40) return 'Medium Friction';
    return 'Low Friction';
  };

  const handleFieldClick = (field, form) => {
    setSelectedField(field);
    setSelectedForm(form);
    setFieldModalVisible(true);
  };

  const handleExportData = (format = 'json') => {
    if (!selectedField || !selectedForm) return;

    const exportData = {
      user_email: formData?.user_email || 'Unknown',
      form_name: selectedForm.form_name,
      field_name: selectedField.field_name,
      export_date: new Date().toISOString(),
      analytics: {
        completion_rate: selectedField.completion_rate,
        abandonment_rate: selectedField.abandonment_rate,
        avg_time_seconds: selectedField.avg_time_seconds,
        total_interactions: selectedField.total_interactions,
        corrections: selectedField.corrections,
        errors: selectedField.errors,
        friction_score: selectedField.friction_score,
        field_type: selectedField.field_type
      }
    };

    if (format === 'json') {
      const dataStr = JSON.stringify(exportData, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
      const exportFileDefaultName = `${selectedForm.form_name}_${selectedField.field_name}_analytics.json`;
      
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
    } else if (format === 'csv') {
      const csvHeaders = ['Metric', 'Value'];
      const csvData = [
        ['User Email', exportData.user_email],
        ['Form Name', exportData.form_name],
        ['Field Name', exportData.field_name],
        ['Export Date', exportData.export_date],
        ['Completion Rate (%)', exportData.analytics.completion_rate],
        ['Abandonment Rate (%)', exportData.analytics.abandonment_rate],
        ['Avg Time (seconds)', exportData.analytics.avg_time_seconds],
        ['Total Interactions', exportData.analytics.total_interactions],
        ['Corrections', exportData.analytics.corrections],
        ['Errors', exportData.analytics.errors],
        ['Friction Score', exportData.analytics.friction_score],
        ['Field Type', exportData.analytics.field_type]
      ];
      
      const csvContent = [csvHeaders, ...csvData].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
      const dataUri = 'data:text/csv;charset=utf-8,'+ encodeURIComponent(csvContent);
      const exportFileDefaultName = `${selectedForm.form_name}_${selectedField.field_name}_analytics.csv`;
      
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
    }
  };

  if (loading) {
    return (
      <div className="abo-content">
        <h2>Form Analytics</h2>
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
          <p style={{ marginTop: 16, color: '#94a3b8' }}>Loading form analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="abo-content">
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h2 style={{ margin: 0 }}>Form Analytics</h2>
            <p style={{ color: '#94a3b8', marginTop: 4 }}>
              Track field-level interactions, abandonment rates, and friction points
            </p>
          </div>
          <Select
            value={selectedWebsite}
            onChange={setSelectedWebsite}
            style={{ width: 250 }}
            placeholder="Select website"
          >
            {websites.map(site => (
              <Option key={site.id} value={site.id}>{site.url}</Option>
            ))}
          </Select>
        </div>
        
        {/* Date Filters */}
        <Card size="small" style={{ background: 'rgba(99, 102, 241, 0.05)', border: '1px solid rgba(99, 102, 241, 0.2)' }}>
          <Space size="middle" wrap>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CalendarOutlined style={{ color: '#6366f1' }} />
              <span style={{ fontWeight: 500 }}>Filter by Date:</span>
            </div>
            
            <DatePicker
              placeholder="Specific Date (e.g., Dec 31, 2025)"
              value={specificDate}
              onChange={(date) => {
                setSpecificDate(date);
                if (date) setDateRange(null);
              }}
              format="MMM DD, YYYY"
              style={{ width: 220 }}
              allowClear
            />
            
            <span style={{ color: '#94a3b8' }}>or</span>
            
            <RangePicker
              placeholder={['Start Date', 'End Date']}
              value={dateRange}
              onChange={(dates) => {
                setDateRange(dates);
                if (dates) setSpecificDate(null);
              }}
              format="MMM DD, YYYY"
              style={{ width: 280 }}
              allowClear
            />
            
            {(specificDate || dateRange) && (
              <Alert
                message={
                  specificDate 
                    ? `Showing data for ${specificDate.format('MMM DD, YYYY')}`
                    : `Showing data from ${dateRange[0].format('MMM DD, YYYY')} to ${dateRange[1].format('MMM DD, YYYY')}`
                }
                type="info"
                showIcon
                closable={false}
                style={{ marginLeft: 8 }}
              />
            )}
          </Space>
        </Card>
      </div>

      {/* Help Section */}
      <Alert
        message={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <QuestionCircleOutlined />
            <strong>Understanding Form Analytics</strong>
          </div>
        }
        description={
          <div>
            <p style={{ marginBottom: 8 }}>
              <strong>Completion Rate:</strong> Percentage of users who successfully filled and submitted the field.
            </p>
            <p style={{ marginBottom: 8 }}>
              <strong>Abandonment Rate:</strong> Percentage of users who started but didn't complete the field.
            </p>
            <p style={{ marginBottom: 8 }}>
              <strong>Friction Score:</strong> Indicates how difficult users find this field (based on time spent, corrections, and abandonment).
            </p>
            <p style={{ marginBottom: 0 }}>
              <strong>Avg. Time:</strong> Average time users spend on this field. Higher times may indicate confusion or complexity.
            </p>
          </div>
        }
        type="info"
        showIcon
        closable
        style={{ marginBottom: 24 }}
      />

      {!formData || formData.forms?.length === 0 ? (
        <Card>
          <Empty
            description={
              <div>
                <p>No form data available yet</p>
                <p style={{ fontSize: 12, color: '#94a3b8' }}>
                  Form analytics will appear here once users interact with forms on your website
                </p>
              </div>
            }
          />
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {formData.forms.map((form, formIndex) => (
            <Card
              key={formIndex}
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{form.form_name || `Form ${formIndex + 1}`}</span>
                  <div style={{ fontSize: 14, fontWeight: 'normal' }}>
                    <span style={{ marginRight: 16 }}>
                      Total Submissions: <strong>{form.total_submissions}</strong>
                    </span>
                    <span>
                      Abandonment Rate: <strong style={{ color: form.abandonment_rate > 50 ? '#ef4444' : '#10b981' }}>
                        {form.abandonment_rate}%
                      </strong>
                    </span>
                  </div>
                </div>
              }
              hoverable
              style={{ borderRadius: 14 }}
            >
              <Row gutter={[16, 16]}>
                {form.fields.map((field, fieldIndex) => (
                  <Col xs={24} sm={12} lg={8} key={fieldIndex}>
                    <Card
                      size="small"
                      style={{
                        borderRadius: 10,
                        border: `2px solid ${getFrictionColor(field.friction_score)}`,
                        background: 'rgba(255,255,255,0.02)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                      onClick={() => handleFieldClick(field, form)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = `0 4px 20px ${getFrictionColor(field.friction_score)}40`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <strong style={{ fontSize: 15 }}>{field.field_name}</strong>
                          <Tooltip title={`Friction Score: ${field.friction_score}/100. ${getFrictionLabel(field.friction_score)}`}>
                            {field.friction_score >= 70 ? (
                              <WarningOutlined style={{ color: '#ef4444', fontSize: 18 }} />
                            ) : field.friction_score >= 40 ? (
                              <QuestionCircleOutlined style={{ color: '#f59e0b', fontSize: 18 }} />
                            ) : (
                              <CheckCircleOutlined style={{ color: '#10b981', fontSize: 18 }} />
                            )}
                          </Tooltip>
                        </div>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>
                          {field.field_type}
                        </div>
                      </div>

                      {/* Completion Rate */}
                      <Tooltip title="Percentage of users who successfully filled and submitted this field">
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ fontSize: 12 }}>Completion Rate</span>
                            <span style={{ fontSize: 12, fontWeight: 600 }}>{field.completion_rate}%</span>
                          </div>
                          <Progress
                            percent={field.completion_rate}
                            strokeColor={field.completion_rate >= 70 ? '#10b981' : field.completion_rate >= 40 ? '#f59e0b' : '#ef4444'}
                            showInfo={false}
                            size="small"
                          />
                        </div>
                      </Tooltip>

                      {/* Abandonment Rate */}
                      <Tooltip title="Percentage of users who started but didn't complete this field">
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ fontSize: 12 }}>Abandonment Rate</span>
                            <span style={{ fontSize: 12, fontWeight: 600, color: '#ef4444' }}>{field.abandonment_rate}%</span>
                          </div>
                          <Progress
                            percent={field.abandonment_rate}
                            strokeColor="#ef4444"
                            showInfo={false}
                            size="small"
                          />
                        </div>
                      </Tooltip>

                      {/* Stats Grid */}
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: '1fr 1fr', 
                        gap: 8,
                        padding: 8,
                        background: 'rgba(0,0,0,0.1)',
                        borderRadius: 6,
                        fontSize: 11
                      }}>
                        <Tooltip title="Average time users spend on this field">
                          <div style={{ cursor: 'help' }}>
                            <div style={{ color: '#94a3b8' }}>Avg. Time</div>
                            <div style={{ fontWeight: 600 }}>{field.avg_time_seconds}s</div>
                          </div>
                        </Tooltip>
                        <Tooltip title="Total clicks, focus, and typing events">
                          <div style={{ cursor: 'help' }}>
                            <div style={{ color: '#94a3b8' }}>Interactions</div>
                            <div style={{ fontWeight: 600 }}>{field.total_interactions}</div>
                          </div>
                        </Tooltip>
                        <Tooltip title="Times users edited their input">
                          <div style={{ cursor: 'help' }}>
                            <div style={{ color: '#94a3b8' }}>Corrections</div>
                            <div style={{ fontWeight: 600, color: field.corrections > 5 ? '#f59e0b' : 'inherit' }}>
                              {field.corrections}
                            </div>
                          </div>
                        </Tooltip>
                        <Tooltip title="Validation errors triggered">
                          <div style={{ cursor: 'help' }}>
                            <div style={{ color: '#94a3b8' }}>Errors</div>
                            <div style={{ fontWeight: 600, color: field.errors > 0 ? '#ef4444' : 'inherit' }}>
                              {field.errors}
                            </div>
                          </div>
                        </Tooltip>
                      </div>

                      {/* Friction Indicator */}
                      {field.friction_score >= 40 && (
                        <Alert
                          message={getFrictionLabel(field.friction_score)}
                          description={
                            field.friction_score >= 70
                              ? 'Users struggle with this field. Consider simplifying or adding help text.'
                              : 'Some users find this field challenging. Review for clarity.'
                          }
                          type={field.friction_score >= 70 ? 'error' : 'warning'}
                          showIcon
                          style={{ marginTop: 12, fontSize: 11 }}
                        />
                      )}
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>
          ))}
        </div>
      )}

      {/* Smart Recommendations Section */}
      {formData && formData.forms?.length > 0 && (
        <Card 
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 18 }}>💡</span>
              <span>Smart Recommendations</span>
            </div>
          }
          style={{ marginTop: 24, borderRadius: 14, background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(168, 85, 247, 0.05) 100%)' }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {(() => {
              const recommendations = [];
              
              // Analyze all forms and fields
              formData.forms.forEach(form => {
                // High abandonment form
                if (form.abandonment_rate > 50) {
                  recommendations.push({
                    type: 'error',
                    priority: 'high',
                    title: `Critical: ${form.form_name} has ${form.abandonment_rate}% abandonment`,
                    description: `More than half of users abandon this form. Consider reducing the number of required fields, breaking it into multiple steps, or reviewing field complexity.`,
                    action: 'Review form structure and field requirements'
                  });
                }
                
                // High friction fields
                const highFrictionFields = form.fields.filter(f => f.friction_score >= 70);
                if (highFrictionFields.length > 0) {
                  recommendations.push({
                    type: 'error',
                    priority: 'high',
                    title: `${highFrictionFields.length} field(s) in ${form.form_name} have high friction`,
                    description: `Fields: ${highFrictionFields.map(f => f.field_name).join(', ')}. Users struggle significantly with these fields. Add help text, examples, or simplify validation rules.`,
                    action: 'Simplify high-friction fields'
                  });
                }
                
                // Medium friction fields
                const mediumFrictionFields = form.fields.filter(f => f.friction_score >= 40 && f.friction_score < 70);
                if (mediumFrictionFields.length > 2) {
                  recommendations.push({
                    type: 'warning',
                    priority: 'medium',
                    title: `${mediumFrictionFields.length} fields in ${form.form_name} show moderate friction`,
                    description: `Fields: ${mediumFrictionFields.map(f => f.field_name).join(', ')}. Consider adding tooltips, placeholder text, or inline validation feedback.`,
                    action: 'Improve field clarity and guidance'
                  });
                }
                
                // Fields with high error rates
                const errorFields = form.fields.filter(f => f.errors > 5);
                if (errorFields.length > 0) {
                  recommendations.push({
                    type: 'error',
                    priority: 'high',
                    title: `Validation errors detected in ${form.form_name}`,
                    description: `${errorFields.length} field(s) have frequent validation errors: ${errorFields.map(f => `${f.field_name} (${f.errors} errors)`).join(', ')}. Review validation rules and provide clearer error messages.`,
                    action: 'Fix validation rules and error messaging'
                  });
                }
                
                // Fields taking too long
                const slowFields = form.fields.filter(f => f.avg_time_seconds > 30);
                if (slowFields.length > 0) {
                  recommendations.push({
                    type: 'warning',
                    priority: 'medium',
                    title: `Users spend excessive time on ${slowFields.length} field(s) in ${form.form_name}`,
                    description: `Fields: ${slowFields.map(f => `${f.field_name} (${f.avg_time_seconds}s avg)`).join(', ')}. Long interaction times suggest confusion. Add examples or auto-complete features.`,
                    action: 'Add guidance or auto-complete'
                  });
                }
                
                // High correction rate
                const correctionFields = form.fields.filter(f => f.corrections > 10);
                if (correctionFields.length > 0) {
                  recommendations.push({
                    type: 'warning',
                    priority: 'medium',
                    title: `High correction rate in ${form.form_name}`,
                    description: `Users frequently correct: ${correctionFields.map(f => `${f.field_name} (${f.corrections} corrections)`).join(', ')}. This indicates unclear requirements or confusing labels.`,
                    action: 'Clarify field labels and requirements'
                  });
                }
                
                // Excellent performing fields (positive feedback)
                const excellentFields = form.fields.filter(f => f.completion_rate >= 90 && f.friction_score < 30);
                if (excellentFields.length > 0 && form.fields.length > 2) {
                  recommendations.push({
                    type: 'success',
                    priority: 'low',
                    title: `${excellentFields.length} field(s) in ${form.form_name} perform excellently`,
                    description: `Fields: ${excellentFields.map(f => f.field_name).join(', ')}. These fields have high completion rates and low friction. Use them as templates for improving other fields.`,
                    action: 'Replicate best practices from these fields'
                  });
                }
                
                // Low overall completion but some fields are good
                if (form.abandonment_rate > 30 && excellentFields.length > 0) {
                  const problemFields = form.fields.filter(f => f.friction_score >= 40 || f.abandonment_rate > 30);
                  if (problemFields.length > 0) {
                    recommendations.push({
                      type: 'info',
                      priority: 'medium',
                      title: `Form optimization opportunity in ${form.form_name}`,
                      description: `While ${excellentFields.length} field(s) perform well, ${problemFields.length} field(s) drag down overall completion. Focus on: ${problemFields.map(f => f.field_name).join(', ')}.`,
                      action: 'Prioritize fixing underperforming fields'
                    });
                  }
                }
              });
              
              // Sort by priority
              const priorityOrder = { high: 0, medium: 1, low: 2 };
              recommendations.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
              
              // Show top 5 recommendations
              const topRecommendations = recommendations.slice(0, 5);
              
              if (topRecommendations.length === 0) {
                return (
                  <Alert
                    message="All Forms Performing Well"
                    description="Your forms show healthy metrics across all fields. Continue monitoring for any changes in user behavior."
                    type="success"
                    showIcon
                  />
                );
              }
              
              return topRecommendations.map((rec, idx) => (
                <Alert
                  key={idx}
                  message={rec.title}
                  description={
                    <div>
                      <p style={{ marginBottom: 8 }}>{rec.description}</p>
                      <strong style={{ fontSize: 12, color: rec.type === 'error' ? '#dc2626' : rec.type === 'warning' ? '#d97706' : '#059669' }}>
                        ⚡ Action: {rec.action}
                      </strong>
                    </div>
                  }
                  type={rec.type}
                  showIcon
                  style={{ marginBottom: idx < topRecommendations.length - 1 ? 12 : 0 }}
                />
              ));
            })()}
          </div>
        </Card>
      )}

      {/* Field Details Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <EyeOutlined style={{ color: '#6366f1' }} />
            <div>
              <div style={{ fontSize: 18, fontWeight: 600 }}>{selectedField?.field_name}</div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                {selectedForm?.form_name} • {selectedField?.field_type}
              </div>
            </div>
          </div>
        }
        open={fieldModalVisible}
        onCancel={() => setFieldModalVisible(false)}
        width={800}
        footer={[
          <Button key="cancel" onClick={() => setFieldModalVisible(false)}>
            Close
          </Button>,
          <Button 
            key="csv" 
            icon={<DownloadOutlined />} 
            onClick={() => handleExportData('csv')}
            style={{ marginLeft: 8 }}
          >
            Download CSV
          </Button>,
          <Button 
            key="json" 
            type="primary" 
            icon={<DownloadOutlined />} 
            onClick={() => handleExportData('json')}
          >
            Download JSON
          </Button>
        ]}
      >
        {selectedField && (
          <div style={{ padding: '20px 0' }}>
            {/* Key Metrics */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Tooltip title="Percentage of users who successfully completed this field out of all who started it">
                  <div>
                    <Statistic
                      title="Completion Rate"
                      value={selectedField.completion_rate}
                      suffix="%"
                      valueStyle={{ color: selectedField.completion_rate >= 70 ? '#10b981' : selectedField.completion_rate >= 40 ? '#f59e0b' : '#ef4444' }}
                    />
                    <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>Users who finished this field</div>
                  </div>
                </Tooltip>
              </Col>
              <Col span={6}>
                <Tooltip title="Percentage of users who started but did not complete this field">
                  <div>
                    <Statistic
                      title="Abandonment Rate"
                      value={selectedField.abandonment_rate}
                      suffix="%"
                      valueStyle={{ color: '#ef4444' }}
                    />
                    <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>Users who left this field</div>
                  </div>
                </Tooltip>
              </Col>
              <Col span={6}>
                <Tooltip title="Average time in seconds users spend interacting with this field">
                  <div>
                    <Statistic
                      title="Avg Time"
                      value={selectedField.avg_time_seconds}
                      suffix="s"
                      precision={1}
                    />
                    <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>Time spent per user</div>
                  </div>
                </Tooltip>
              </Col>
              <Col span={6}>
                <Tooltip title="Composite score (0-100) measuring field difficulty based on time, errors, and corrections. Higher = more difficult">
                  <div>
                    <Statistic
                      title="Friction Score"
                      value={selectedField.friction_score}
                      suffix="/100"
                      valueStyle={{ 
                        color: selectedField.friction_score >= 70 ? '#ef4444' : 
                               selectedField.friction_score >= 40 ? '#f59e0b' : '#10b981' 
                      }}
                    />
                    <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>Field difficulty score</div>
                  </div>
                </Tooltip>
              </Col>
            </Row>

            <Divider />

            {/* Detailed Breakdown */}
            <Row gutter={[24, 24]}>
              <Col span={12}>
                <Card size="small" title="Performance Metrics">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: 12 }}>Completion Rate</span>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>{selectedField.completion_rate}%</span>
                      </div>
                      <Progress
                        percent={selectedField.completion_rate}
                        strokeColor={selectedField.completion_rate >= 70 ? '#10b981' : selectedField.completion_rate >= 40 ? '#f59e0b' : '#ef4444'}
                        showInfo={false}
                        size="small"
                      />
                    </div>

                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: 12 }}>Abandonment Rate</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#ef4444' }}>{selectedField.abandonment_rate}%</span>
                      </div>
                      <Progress
                        percent={selectedField.abandonment_rate}
                        strokeColor="#ef4444"
                        showInfo={false}
                        size="small"
                      />
                    </div>
                  </div>
                </Card>
              </Col>

              <Col span={12}>
                <Card size="small" title="Interaction Analysis">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <Tooltip title="Total number of times users clicked, focused, or typed in this field">
                      <div style={{ textAlign: 'center', cursor: 'help' }}>
                        <div style={{ fontSize: 24, fontWeight: 800, color: '#6366f1' }}>{selectedField.total_interactions}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>Total Interactions</div>
                        <div style={{ fontSize: 9, color: '#cbd5e1', marginTop: 2 }}>Clicks + Focus + Typing</div>
                      </div>
                    </Tooltip>
                    <Tooltip title="Number of times users changed their input after initially filling the field">
                      <div style={{ textAlign: 'center', cursor: 'help' }}>
                        <div style={{ fontSize: 24, fontWeight: 800, color: selectedField.corrections > 5 ? '#f59e0b' : '#10b981' }}>{selectedField.corrections}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>Corrections</div>
                        <div style={{ fontSize: 9, color: '#cbd5e1', marginTop: 2 }}>User edits after input</div>
                      </div>
                    </Tooltip>
                    <Tooltip title="Number of validation errors triggered by user input">
                      <div style={{ textAlign: 'center', cursor: 'help' }}>
                        <div style={{ fontSize: 24, fontWeight: 800, color: selectedField.errors > 0 ? '#ef4444' : '#10b981' }}>{selectedField.errors}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>Errors</div>
                        <div style={{ fontSize: 9, color: '#cbd5e1', marginTop: 2 }}>Validation failures</div>
                      </div>
                    </Tooltip>
                    <Tooltip title="Average time users spend on this field from focus to completion">
                      <div style={{ textAlign: 'center', cursor: 'help' }}>
                        <div style={{ fontSize: 24, fontWeight: 800, color: '#8b5cf6' }}>{selectedField.avg_time_seconds}s</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>Avg Time</div>
                        <div style={{ fontSize: 9, color: '#cbd5e1', marginTop: 2 }}>Focus to completion</div>
                      </div>
                    </Tooltip>
                  </div>
                </Card>
              </Col>
            </Row>

            <Divider />

            {/* Insights and Recommendations */}
            <Card size="small" title="Insights & Recommendations" style={{ marginTop: 24 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {selectedField.friction_score >= 70 && (
                  <Alert
                    message="High Friction Detected"
                    description="This field shows significant user struggle. Consider simplifying the field requirements, adding helpful hints, or breaking it into multiple steps."
                    type="error"
                    showIcon
                  />
                )}

                {selectedField.friction_score >= 40 && selectedField.friction_score < 70 && (
                  <Alert
                    message="Medium Friction Detected"
                    description="Users find this field somewhat challenging. Review field clarity and consider adding examples or better labeling."
                    type="warning"
                    showIcon
                  />
                )}

                {selectedField.abandonment_rate > 20 && (
                  <Alert
                    message="High Abandonment Rate"
                    description={`Users abandon this field ${selectedField.abandonment_rate}% of the time. This field may be too complex or placed too early in the form.`}
                    type="warning"
                    showIcon
                  />
                )}

                {selectedField.avg_time_seconds > 30 && (
                  <Alert
                    message="Long Interaction Time"
                    description={`Users spend ${selectedField.avg_time_seconds} seconds on average. Consider if this field can be simplified or if additional guidance is needed.`}
                    type="info"
                    showIcon
                  />
                )}

                {selectedField.errors > 0 && (
                  <Alert
                    message="Field Errors Detected"
                    description={`${selectedField.errors} validation errors occurred. Review field validation rules and user feedback.`}
                    type="error"
                    showIcon
                  />
                )}

                {selectedField.completion_rate >= 90 && selectedField.friction_score < 30 && (
                  <Alert
                    message="Excellent Performance"
                    description="This field performs very well with high completion rates and low friction. Consider using this as a model for other fields."
                    type="success"
                    showIcon
                  />
                )}
              </div>
            </Card>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default FormAnalytics;
